// src/components/SystemInformation.js

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Divider,
    Typography,
    Avatar,
    useTheme,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack
} from '@mui/material';
import {
    Edit as EditIcon,
    CalendarMonth,
    Update as UpdateIcon,
    Info,
    PersonAdd,
    Group,
    DateRange
} from '@mui/icons-material';
import { formatCreatedAtDate } from '../utils/helpers';

const SystemInformation = ({
    createdBy,
    updatedBy,
    createdAt,
    updatedAt,
    title = 'System Information',
    showDivider = true,
    variant = 'outlined',
    bgColor = null,
    compact = false,
    customFields = [],
    showIcons = true,
    iconSize = 'small',
    avatarSize = 36,
    ...props
}) => {
    const theme = useTheme();

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return formatCreatedAtDate(date);
    };

    const getFieldValue = (value) => {
        return value || 'N/A';
    };

    // Render an individual listing item
    const renderListItem = (field) => {
        const { icon, label, value, color, customValue } = field;
        const displayValue = customValue !== null ? customValue : getFieldValue(value);

        return (
            <ListItem
                key={label}
                disablePadding
                sx={{
                    p: compact ? 0.75 : 1.25,
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    }
                }}
            >
                {showIcons && (
                    <ListItemAvatar sx={{ minWidth: avatarSize + 12 }}>
                        <Avatar
                            sx={{
                                width: avatarSize,
                                height: avatarSize,
                                bgcolor: theme.palette[color].light + '40',
                                border: `2px solid ${theme.palette[color].main + '30'}`,
                            }}
                        >
                            {React.cloneElement(icon, {
                                fontSize: iconSize,
                                sx: {
                                    color: theme.palette[color].main,
                                    opacity: 0.9
                                }
                            })}
                        </Avatar>
                    </ListItemAvatar>
                )}
                <ListItemText
                    primary={
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                display: 'block',
                                mb: 0.25
                            }}
                        >
                            {label}
                        </Typography>
                    }
                    secondary={
                        <Typography
                            variant={compact ? 'body2' : 'body1'}
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: compact ? '0.875rem' : '1.0rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            {displayValue}
                            {label === 'Created By' && displayValue !== 'N/A' && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: theme.palette.success.main,
                                        ml: 0.5
                                    }}
                                />
                            )}
                            {label === 'Updated By' && displayValue !== 'N/A' && displayValue !== createdBy && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: theme.palette.warning.main,
                                        ml: 0.5
                                    }}
                                />
                            )}
                        </Typography>
                    }
                />
            </ListItem>
        );
    };

    // User-related fields
    const userFields = [
        {
            icon: <PersonAdd />,
            label: 'Created By',
            value: createdBy,
            color: 'primary',
            customValue: null
        },
        {
            icon: <EditIcon />,
            label: 'Updated By',
            value: updatedBy,
            color: 'warning',
            customValue: null
        }
    ];

    // Date-related fields
    const dateFields = [
        {
            icon: <CalendarMonth />,
            label: 'Created Date',
            value: createdAt,
            color: 'success',
            customValue: formatDate(createdAt)
        },
        {
            icon: <UpdateIcon />,
            label: 'Last Updated',
            value: updatedAt,
            color: 'info',
            customValue: formatDate(updatedAt)
        }
    ];

    return (
        <>
            {showDivider && (
                <Divider sx={{ 
                    my: 3,
                    '&::before, &::after': {
                        borderColor: theme.palette.divider
                    }
                }} />
            )}
            
            <Box sx={{ mb: 2 }}>
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -4,
                            left: 0,
                            width: 40,
                            height: 3,
                            borderRadius: 2,
                            backgroundColor: theme.palette.primary.main,
                            opacity: 0.3
                        }
                    }}
                >
                    <Info fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    {title}
                </Typography>
            </Box>

            <Card 
                variant={variant} 
                sx={{ 
                    bgcolor: bgColor || theme.palette.background.default,
                    borderRadius: 2,
                    borderColor: theme.palette.divider,
                    transition: 'box-shadow 0.3s ease',
                    '&:hover': {
                        boxShadow: theme.shadows[2]
                    },
                    ...props.sx
                }}
            >
                <CardContent sx={{ 
                    p: compact ? 2 : 3,
                    '&:last-child': {
                        pb: compact ? 2 : 3
                    }
                }}>
                    <Stack 
                        direction={{ xs: 'column', md: 'row' }} 
                        spacing={4} 
                        divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
                    >
                        {/* Users List Group */}
                        <Box sx={{ flex: 1 }}>
                            <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                    fontWeight: 700, 
                                    color: theme.palette.text.secondary, 
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Group fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                                Users
                            </Typography>
                            <List disablePadding>
                                {userFields.map((field) => renderListItem(field))}
                            </List>
                        </Box>

                        {/* Dates List Group */}
                        <Box sx={{ flex: 1 }}>
                            <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                    fontWeight: 700, 
                                    color: theme.palette.text.secondary, 
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <DateRange fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                                Dates
                            </Typography>
                            <List disablePadding>
                                {dateFields.map((field) => renderListItem(field))}
                            </List>
                        </Box>

                        {/* Custom Fields List Group (Optional) */}
                        {customFields.length > 0 && (
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        color: theme.palette.text.secondary, 
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Info fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                                    Additional Information
                                </Typography>
                                <List disablePadding>
                                    {customFields.map((field) => renderListItem({
                                        icon: field.icon || <Info />,
                                        label: field.label,
                                        value: field.value,
                                        color: field.color || 'primary',
                                        customValue: field.customValue || null
                                    }))}
                                </List>
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </>
    );
};

export default SystemInformation;